import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { statsSchema } from "zodios/common/statsSchema";
import { advertisementStatsSchema } from "./v4Query";
import { columnFilterSchema } from "zodios/common/columnFilterSchema";

const adPropertyUpdateSchema = z.object({
  custom_api_request: z.literal("update_advertisement"),
  id: z.number(),

  // Can pass any or all of these fields, omitted won't be updated.
  name: z.string().optional(),
  max_acos: z.number().optional(),
  target_acos: z.number().optional(),
  daily_budget: z.number().optional(),
  state: z.number().optional(),
});

const atomicUpdateV2Schema = z.union([
  z.object({
    field: z.literal("tags"),
    action: z.union([z.literal("set"), z.literal("remove"), z.literal("add")]),
    value: z.string().array(),
  }),
  z.object({
    field: z.literal("cancel_scheduled_actions"),
    action: z.literal("set"),
    value: z.literal("cancelled"),
  }),
  z.object({
    field: z.union([
      z.literal("daily_budget"),
      z.literal("target_acos"),
      z.literal("max_acos"),
      z.literal("target_roas"),
      z.literal("min_roas"),
    ]),
    action: z.union([z.literal("set"), z.literal("percent"), z.literal("fixed")]),
    value: z.number(),
  }),
  z.object({
    field: z.union([z.literal("bidding_rules"), z.literal("budget_rules"), z.literal("category")]),
    action: z.literal("set"),
    value: z.string().or(z.number()).or(z.null()), // ID, normally a number, but backend will coerce
  }),
  z.object({
    field: z.union([
      z.literal("bid_adjustments_by_placement"),
      z.literal("shopper_cohort_bidding"),
      z.literal("bid_adjustments_by_shopper_segment"),
    ]),
    action: z.literal("set"),
    value: z.record(z.string().regex(/^\d+$/), z.array(z.object({}).passthrough())),
  }),
  z.object({
    field: z.union([z.literal("bid_algo"), z.literal("sales_volume_control_slider")]),
    action: z.literal("set"),
    value: z.record(z.string().regex(/^\d+$/), z.union([z.string(), z.number(), z.null()])),
  }),
  z.object({
    field: z.union([z.literal("non_trellis_enrolled_ad"), z.literal("non_trellis_enrolled_ad_accept_bids")]),
    action: z.literal("set"),
    value: z.boolean(),
  }),
]);

const adPropertyUpdateSchemaV2 = z.object({
  advertisements: z.array(z.number()),
  updates: z.array(atomicUpdateV2Schema),
});

const adDaypartingUpdateSchema = z.object({
  advertisement_ids: z.array(z.number()),
  day_parting_method: z.any(),
  day_parting_multipliers: z.any(),
});

const adScheduledActionsSchema = z.object({
  channel: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  status: z.string(),
  gvadvertisements_set: z.array(z.number()),
  fields: z.array(
    z.object({
      field_name: z.string(),
      change_type: z.string(),
      change_value: z.number(),
    }),
  ),
});

const adTargetUpdateSchema = z.object({
  data: z.object({
    advertisement: z.number(),
    channel: z.string(),
    target_type: z.string(),
    action: z.literal("create_targets"),
    ad_group: z.optional(z.string()),
    targets: z.optional(
      z.union([
        z.array(
          z.object({
            text: z.string(),
            matchTypes: z.array(z.string()),
          }),
        ),
        z.object({
          products: z.array(z.string()),
          targetTypes: z.array(z.string()),
        }),
      ]),
    ),
  }),
});

const adGroupSchema = z.object({
  ad_group_id: z.string(),
  ad_group_name: z.string(),
});

export type AdPropertyUpdate = z.infer<typeof adPropertyUpdateSchema>;
export type BulkAdPropertyUpdate = z.infer<typeof adPropertyUpdateSchemaV2>;
export type BulkAdDaypartingUpdate = z.infer<typeof adDaypartingUpdateSchema>;
export type BulkAdScheduledActionsUpdate = z.infer<typeof adScheduledActionsSchema>;
export type AddTargetsToAdUpdate = z.infer<typeof adTargetUpdateSchema>;

export const adsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "setAdState",
    method: "post",
    path: "/set_campaign_state/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ id: z.number().or(z.string()), state: z.literal(0).or(z.literal(1)) }),
      },
    ],
    status: 200,
    response: z.object({
      new_campaign_state: z.number(),
    }),
    errors: [
      {
        status: 400,
        schema: z.object({}),
      },
    ],
  })
    .addEndpoint({
      alias: "setAdStateBulk",
      method: "post",
      path: "/set_campaign_state_bulk/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({
            ad_ids: z.array(z.number()),
            state: z.literal("ENABLED").or(z.literal("PAUSED")),
            marketplace: z.string(),
          }),
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAdProperties",
      method: "put",
      // "custom_api_request" isn't actually a query parameter that backend accepts, backend expects it in the body. We're using it to differentiate between different ways to call this endpoint
      path: "/gvads/advproduct/?custom_api_request=update_advertisement",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adPropertyUpdateSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAdPropertiesV2",
      method: "post",
      path: "/ads/bulk_update/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adPropertyUpdateSchemaV2,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateDayparting",
      method: "post",
      path: "/bulk_update_dayparting/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adDaypartingUpdateSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAdScheduledActions",
      method: "post",
      path: "/scheduledactions/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adScheduledActionsSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "addTargetsToAd",
      method: "post",
      path: "/target_actions/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adTargetUpdateSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "getAdGroups",
      method: "get",
      path: "/ad_groups",
      status: 200,
      response: z.array(adGroupSchema),
      parameters: [
        {
          name: "advertisement",
          type: "Query",
          schema: z.number(),
        },
      ],
    })
    .addEndpoint({
      alias: "getBiddingPrograms",
      method: "get",
      path: "/programs?engine=bidding",
      status: 200,
      response: z.object({
        results: z.array(
          z.object({
            id: z.number(),
            name: z.string(),
          }),
        ),
      }),
    })
    .build(),
);

export const adReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4AdvReportByAd",
    method: "get",
    path: "/v4/query/?table=advertisement&grouping=ad_id",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            ad_id: z.number(),
            ad_name: z.string(),
            created_datetime: z.string().datetime({ local: true }),
            ad_goal: z.number(),
            state: z.number(),
            category_id: z.number(),
            ad_type: z.number(),
            auto_campaign_enabled: z.boolean(),
            non_trellis_enrolled_ad: z.boolean(),
            non_trellis_enrolled_ad_accept_bids: z.boolean(),
            ad_status: z.number(),
            daily_budget: z.coerce.number(),
            monthly_budget: z.coerce.number(),
            ad_source: z.string(),
            managed: z.string(),
            is_composite: z.boolean().optional(),
            serving_status: z.string(),

            max_acos: z.coerce.number(),
            target_acos: z.coerce.number(),
            min_roas: z.coerce.number(),
            target_roas: z.coerce.number(),

            trend_up: z.boolean(),
            trend_down: z.boolean(),
            serving_low: z.boolean(),
            recent: z.boolean(),
            high_acos: z.boolean().optional(),
            low_acos: z.boolean().optional(),
            in_range_acos: z.boolean().optional(),
            active_ad: z.boolean(),
            tags: z.string().transform((tags) => {
              try {
                return JSON.parse(tags) as string[];
              } catch {
                return [];
              }
            }),
            scheduled_actions: z.array(
              z.object({
                scheduled_action_start_date: z.string().optional(),
                scheduled_action_end_date: z.string().optional(),
                scheduled_action_fields: z.string().transform((spec) => {
                  try {
                    return JSON.parse(spec) as {
                      change_type?: string;
                      change_value?: number;
                    }[];
                  } catch {
                    return [];
                  }
                }),
                scheduled_action_status: z.string().optional(),
              }),
            ),
            ntm_enrollment_status: z.string().nullable(),
            program_id: z.number().nullable(),
            program_name: z.string().nullable(),
            day_parting_method: z.number().nullable(),
          })
          .merge(statsSchema)
          .merge(advertisementStatsSchema),
      ),
      cursor: z
        .object({
          hasPreviousPage: z.boolean(),
          hasNextPage: z.boolean(),
          startCursor: z.string().nullable(),
          endCursor: z.string().nullable(),
        })
        .nullable(),
      count: z.number(),
      count_details: z.object({
        count_total: z.number(),
        count_distinct: z.number(),
        trend_up_count: z.number(),
        trend_down_count: z.number(),
        serving_low_count: z.number(),
        recent_count: z.number(),
        high_acos_count: z.number().optional(),
        low_acos_count: z.number().optional(),
        in_range_acos_count: z.number().optional(),
        active_ad_count: z.number().optional(),
      }),
    }),
  })
    .addEndpoint({
      alias: "getV4AdReportExport",
      method: "get",
      path: "/v4/query/?table=advertisement&xls=true&report_type=ad_report",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .addEndpoint({
      alias: "getV4AdvReportByDate",
      method: "get",
      path: "/v4/query/?table=advertisement&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(advertisementStatsSchema),
        ),
        cursor: z
          .object({
            hasPreviousPage: z.boolean(),
            hasNextPage: z.boolean(),
            startCursor: z.string().nullable(),
            endCursor: z.string().nullable(),
          })
          .nullable(),
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=advertisement",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(advertisementStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportOptions",
      method: "get",
      path: "/v4/query/values/?table=advertisement",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.record(z.array(z.string())),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportByAdType",
      method: "get",
      path: "/v4/query/?table=advertisement&grouping=ad_type",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              ad_type: z.number(),
              active_ad_count: z.number().nullable(),
            })
            .merge(statsSchema)
            .merge(advertisementStatsSchema),
        ),
      }),
    })
    .build(),
);
